import classNames from "classnames";
import { useRef, useState, useEffect } from "react";
import styled from "styled-components/macro";
import { useQueryClient } from "@tanstack/react-query";
import { touchSkipioApi, useSkipioApi } from "../../api";
import {
  Box,
  BoxContent,
  BoxHeader,
  BoxNavLeft,
  BoxNavSearch,
  BoxTitle,
} from "../Box";
import Button from "../Button";
import Input, { ErrorMessage } from "../Input";
import Loader from "../Loader";
import { Menu, MenuButtonItem, MenuOverlay } from "../Menu";
import Stack from "../Stack";
import useDebounce from "../../hooks/use-debounce";
import areaCodes from "./area-codes.json";
import { useSkipioQuery } from "../../hooks/use-query";

export default function RequestPhoneNumberInput({
  title,
  numberQuery: initialNumberQuery = "",
  value,
  onChange,
  errors = "",
  footer,
  disabled = false,
}) {
  const queryClient = useQueryClient();
  const [hasCustomSearch, setHasCustomSearch] = useState(false);
  const [seekedNumbers, setSeekedNumbers] = useState([]);
  const [numberQuery, setNumberQuery] = useState(initialNumberQuery);
  const [showSearch, setShowSearch] = useState(!numberQuery);
  const validatedNumberQuery = Number(
    useDebounce(useValueIf(numberQuery, (n) => String(n).length === 3))
  );

  const [state = "", stateAreaCodes = []] =
    Object.entries(areaCodes).find(([_, codes]) => {
      return codes.includes(validatedNumberQuery);
    }) || [];

  const siblingAreaCodes = [...stateAreaCodes];
  siblingAreaCodes.splice(stateAreaCodes.indexOf(validatedNumberQuery), 1);

  const availablePhoneNumbersQuery = useSkipioQuery({
    queryKey: ["apiPhoneNumberSearches", { areaCode: validatedNumberQuery }],
    suspense: false,
    useErrorBoundary: true,
    enabled: !!validatedNumberQuery,
  });
  const numberOptions = availablePhoneNumbersQuery.data?.slice(0, 3) || [];
  const loadingPhoneNumbers =
    availablePhoneNumbersQuery.isLoading &&
    availablePhoneNumbersQuery.fetchStatus === "fetching";

  // make sure searches are stale on unmount
  useEffect(() => {
    return () => {
      touchSkipioApi("apiPhoneNumberSearches");
      queryClient.invalidateQueries({ queryKey: ["apiPhoneNumberSearches"] });
    };
  }, []);

  useEffect(() => {
    if (loadingPhoneNumbers || hasCustomSearch) return;
    if (numberOptions.length > 0) return;

    if (!seekedNumbers.includes(validatedNumberQuery))
      setSeekedNumbers((nums) => [...nums, validatedNumberQuery]);

    const nextSibling = siblingAreaCodes.find(
      (code) => !seekedNumbers.includes(code)
    );

    if (nextSibling) {
      setNumberQuery(String(nextSibling));
    } else if (initialNumberQuery) {
      setNumberQuery(initialNumberQuery);
      setHasCustomSearch(true);
    }
  });

  return (
    <Box>
      {Boolean(title) && (
        <BoxHeader white stacked>
          <BoxTitle>{title}</BoxTitle>
        </BoxHeader>
      )}
      <BoxHeader white={Boolean(title)}>
        {showSearch ? (
          <BoxNavLeft>
            <BoxNavSearch
              disabled={disabled}
              dark={Boolean(title)}
              placeholder="Search numbers by area code (e.g. 800)"
              value={numberQuery}
              onChange={(e) => {
                setNumberQuery(e.target.value.slice(0, 3).replace(/\D/g, ""));
                setHasCustomSearch(true);
              }}
            />
          </BoxNavLeft>
        ) : (
          <BoxNavLeft style={{ padding: "0 6px" }}>
            <Input
              disabled={disabled}
              style={{ width: "100%" }}
              type="select"
              value={numberQuery}
              onChange={(value) => {
                if (!value) {
                  setShowSearch(true);
                } else {
                  setNumberQuery(value.slice(0, 3).replace(/\D/g, ""));
                  setHasCustomSearch(true);
                }
              }}
            >
              <option value={validatedNumberQuery}>
                ({validatedNumberQuery})
              </option>
              <optgroup label={`Other ${state} area codes`}>
                {siblingAreaCodes.map((code) => (
                  <option value={code} key={code}>
                    ({code})
                  </option>
                ))}
              </optgroup>
              <option value="">Enter a specific area code</option>
            </Input>
          </BoxNavLeft>
        )}
      </BoxHeader>

      <BoxContent>
        {loadingPhoneNumbers ? (
          <Empty>
            <Loader />
          </Empty>
        ) : numberOptions.length === 0 ? (
          <Empty>
            <div style={{ margin: "auto", textAlign: "left" }}>
              <Stack space={2}>
                <>
                  Uh-oh! It looks like there aren’t currently any numbers
                  available with that area code, but no worries!
                </>
                <>Let’s choose a different area code and keep rolling 👍</>
                <div style={{ textAlign: "center" }}>
                  <MenuOverlay
                    trigger={({ show, isShowing, ref }) => (
                      <Button
                        variant="link"
                        ref={ref}
                        onClick={show}
                        className={classNames({ active: isShowing })}
                      >
                        Other {state} area codes
                      </Button>
                    )}
                    content={() => (
                      <>
                        {stateAreaCodes.map((code) => (
                          <MenuButtonItem
                            className={
                              String(code) === String(numberQuery)
                                ? "active"
                                : ""
                            }
                            onClick={(e) => {
                              e.preventDefault();
                              setNumberQuery(code);
                              setHasCustomSearch(true);
                              setShowSearch(false);
                            }}
                          >
                            ({code})
                          </MenuButtonItem>
                        ))}
                      </>
                    )}
                  />
                </div>
              </Stack>
            </div>
          </Empty>
        ) : (
          <>
            <Menu>
              {numberOptions.map((entry) => (
                <MenuButtonItem
                  key={entry.apiPhoneNumber}
                  disabled={disabled}
                  onClick={() => {
                    if (disabled) return;
                    onChange({
                      apiPhoneNumber: entry.apiPhoneNumber,
                      apiPhoneProvider: entry.apiPhoneProvider,
                    });
                  }}
                  className={classNames({
                    active: value.apiPhoneNumber === entry.apiPhoneNumber,
                  })}
                >
                  {entry.nationalNumber}
                </MenuButtonItem>
              ))}
            </Menu>
            <Center>
              <Button
                variant="link"
                onClick={async () => {
                  onChange({
                    apiPhoneNumber: "",
                    apiPhoneProvider: "",
                  });
                  await touchSkipioApi("apiPhoneNumberSearches");
                  await queryClient.invalidateQueries({
                    queryKey: ["apiPhoneNumberSearches"],
                  });
                }}
              >
                Show More
              </Button>
            </Center>
          </>
        )}
        {Boolean(errors) && <ErrorMessage>{errors}</ErrorMessage>}
      </BoxContent>
      {footer}
    </Box>
  );
}

export function usePurchasePhoneNumber() {
  const skipioApi = useSkipioApi();

  return async function purchasePhoneNumber({
    apiPhoneProvider,
    apiPhoneNumber,
  }) {
    return await skipioApi.apiPhoneNumberPurchases.post(
      {},
      { data: { apiPhoneProvider, apiPhoneNumber } }
    );
  };
}

function useValueIf(value, check) {
  const ref = useRef(undefined);

  if (check(value)) {
    ref.current = value;
    return value;
  }

  return ref.current;
}

const Center = styled.div`
  text-align: center;
`;

const Empty = styled.div`
  text-align: center;
  padding: 32px;
  line-height: 1.6em;
  ${Loader} {
    height: 30px;
    width: 30px;
    margin: 0 auto;
  }
`;
